import { BQData } from "./app";
import { ApiBQResponse, ApiErrorResponse, ApiBQQuery, ApiBQTable } from "./api_data";
import { Ajax } from "../../shared/ajax";
import { cloneDeep } from 'lodash';
import { Display, FilterConnection } from "./display";
import { Editor } from "./editor";
const indent = "  ";

export class BQ {

  static skip_col(col: BQData): boolean {
    return (col.pivoted && !BQ.date_col(col.column_type)) || col.hidden;
  }

  static date_col(column_type: string): boolean {
    const date = column_type.indexOf("DATE") != -1;
    const timestamp = column_type.indexOf("TIMESTAMP") != -1;
    return date || timestamp;
  }

  static numeric_col(column_type: string): boolean {
    const float = column_type.indexOf("FLOAT") != -1;
    const int = column_type.indexOf("INT") != -1;
    return float || int;
  }

  static field_name(str: string): string {
    let clean = str.replace(/-/g, "_");
    clean = clean.replace(/ /g, "_");
    clean = clean.replace(/[^a-zA-Z0-9_]+/g, "");
    return "f_" + clean;
  }

  static date_extract(facet: string): string {
    switch (facet) {
      case "Day Of Week":
        return "DAYOFWEEK";

      case "Month Of Year":
        return "MONTH";

      default:
        return "QUARTER";
    }
  }

  static date_pivot_number(value: string): number {
    switch (value) {
      case "Sunday":
        return 1;

      case "Monday":
        return 2;

      case "Tuesday":
        return 3;

      case "Wednesday":
        return 4;

      case "Thursday":
        return 5;

      case "Friday":
        return 6;

      case "Saturday":
        return 7;
    }
    return 0;
  }

  static col_agg(col: BQData): string {
    let op = "SUM";
    if (col.operation) {
      op = col.operation;
    }
    return op.toUpperCase() + "(" + col.column_name + ") as " + col.column_name;
  }

  static pivot_select(viz: Display, col: BQData, target: string, column_name: string): string {
    if (!col.pivot) {
      return "";
    }
    const select = function (query: string): string {
      if (viz.group_by) {
        query = "SUM(" + query + ")";
      }
      query = query + " AS " + column_name;
      return query;
    }

    let s = "";
    if (col.column_type === "STRING") {
      s = "IF (" + col.column_name + "='" + col.pivot + "', " + target + ", 0)";
      s = select(s);
    } else if (BQ.date_col(col.column_type) && col.pivot_facet) {
      const extract = BQ.date_extract(col.pivot_facet);
      const compare = BQ.date_pivot_number(col.pivot);
      s = "IF (EXTRACT(" + extract + " FROM " + col.column_name + ")=" + compare + ", " + target + ", 0)";
      s = select(s);
    }
    return s;
  }

  static date_where(f: FilterConnection): string[] {
    let where_strs: string[] = [];
    switch (f.date_mode) {
      case Editor.date_recent.value:
        {
          const date_part = f.date_recent_interval === "ISOWeek" ? " Week" : f.date_recent_interval;
          let extract = "DATE(" + f.column + ")";
          let sub = "DATE_SUB";
          let current = "CURRENT_DATE()";
          if (date_part === "Hour") {
            extract = f.column;
            sub = "TIMESTAMP_SUB"
            current = "CURRENT_TIMESTAMP()"
          }
          const s = extract + " >= " + sub + "(" + current + ", INTERVAL " + f.date_recent_count + " " + date_part + ")";
          where_strs.push(s);
        }

        break;

      case Editor.date_current.value:
        {
          const s = "DATE(" + f.column + ")" + " >= " + "DATE_TRUNC(CURRENT_DATE()," + f.date_current_interval + ")";
          where_strs.push(s);
        }

      default: // custom range
        {
          if (f.date_min) {
            const s = "DATE(" + f.column + ")" + " >= '" + f.date_min + "'";
            where_strs.push(s);
          }
          if (f.date_max) {
            const s = "DATE(" + f.column + ")" + " < '" + f.date_max + "'";
            where_strs.push(s);
          }
        }
        break;
    }
    return where_strs;
  }

  static text_where(f: FilterConnection): string[] {
    let where_strs: string[] = [];
    const q = f.text_query ? f.text_query : "";
    const op = f.text_mode === Editor.text_match.value ? "=" : "<>";
    const s = f.column + " " + op + " '" + q + "'";
    where_strs.push(s);
    return where_strs;
  }

  static where(viz: Display): string {
    let where_strs: string[] = [];
    for (const f of viz.filters) {
      let col: BQData | null = null;
      for (const c of viz.selected_columns) {
        if (c.data.column_name === f.column) {
          col = c.data;
          break;
        }
      }
      if (col) {
        if (BQ.date_col(col.column_type)) {
          where_strs = where_strs.concat(BQ.date_where(f));
        } else if (col.column_type === "STRING") {
          where_strs = where_strs.concat(BQ.text_where(f));
        }
      }
    }
    if (where_strs.length > 0) {
      return "WHERE\n" + indent + where_strs.join("\n" + indent + "AND ") + "\n";
    } else {
      return "";
    }
  }

  static build_query(viz: Display): string {
    let s = "";

    if (viz.query_columns.length == 0) {
      return s;
    }

    let default_sort_col: string | null = null;
    for (const col of viz.query_columns) {
      if (BQ.date_col(col.column_type)) {
        default_sort_col = col.column_name;
        break;
      }
    }

    let has_agg = false;
    for (const sel of viz.query_columns) {
      if (sel.operation) {
        has_agg = true;
      }
    }

    const query_has_group_or_agg = viz.group_by || has_agg;
    let use_group_by = false;

    let select_cols: string[] = [];
    let select_col_names: string[] = [];

    for (const sel of viz.query_columns) {
      // skip pivoted columns (original pivot column), and hidden
      if (BQ.skip_col(sel)) {
        continue;
      }

      if (sel.column_type === "count") {
        let target = "*";
        let as = "Count";
        if (viz.count_target) {
          target = "DISTINCT " + viz.count_target;
          as += "_" + viz.count_target;
        }
        const c = "COUNT(" + target + ") as " + as;
        select_cols.push(c);
        continue;
      }

      let col = sel.column_name;
      let col_name = sel.column_name;
      if (sel.operation) {
        col = BQ.col_agg(sel);
      }
      if (query_has_group_or_agg) {
        if (sel.column_name === viz.group_by) {
          // only date grouping currently
          if (BQ.date_col(sel.column_type)) {
            use_group_by = true;
            let c = col;
            if (sel.column_type == "TIMESTAMP") {
              c = "DATE(" + c + ")";
            }
            col = "DATE_TRUNC(" + c + ", " + viz.group_by_interval + ") as " + sel.column_name;
          }
        } else {
          if (sel.column_type === "STRING") {
            col = "ARRAY_AGG(" + sel.column_name + ") as " + sel.column_name;
          } else if (BQ.date_col(sel.column_type)) {
            col = "ANY_VALUE(" + sel.column_name + ") as " + sel.column_name;
          } else {
            col = BQ.col_agg(sel);
          }
        }
      }
      if (sel.pivot) {
        let val = BQ.field_name(sel.pivot);
        let target = "1"
        if (sel.pivot_target) {
          target = sel.pivot_target
          val = val + "_" + target
        }
        col_name = val;
        col = BQ.pivot_select(viz, sel, target, col_name);
      }
      select_cols.push(col);
      select_col_names.push(col_name);
    }

    if (!default_sort_col && select_col_names.length > 0) {
      default_sort_col = select_col_names[0];
    }

    let table = "";
    if (viz.selected_table) {
      table = viz.selected_table.table.dataset_id + "." + viz.selected_table.table.id;
    }

    s = "SELECT\n";
    s += indent + select_cols.join(",\n" + indent) + "\n"
    s += "FROM\n";
    s += indent + table + "\n"

    if (viz.filters.length > 0) {
      s += BQ.where(viz);
    }

    if (viz.group_by && use_group_by) {
      s += "GROUP BY\n" + indent + viz.group_by + "\n";
    }
    if (viz.sort) {
      let sort_col_included = false;
      for (const col of viz.query_columns) {
        if (col.column_name === viz.sort) {
          sort_col_included = true;
          break;
        }
      }
      const sort_col = sort_col_included ? viz.sort : default_sort_col;
      if (sort_col) {
        const order = viz.sort_order ? viz.sort_order : "ASC";
        s += "ORDER BY\n" + indent + sort_col + " " + order + "\n";
      }
    }
    s += "LIMIT 500";

    return s;
  }

  static run(table: ApiBQTable, viz: Display, cb: Function, error_cb: Function) {
    if (viz.query_columns.length === 0) {
      cb();
      return;
    }
    const cols: BQData[] = [];
    for (const s of viz.query_columns) {
      if (BQ.skip_col(s)) {
        continue;
      }
      const c = cloneDeep(s);
      cols.push(c);
    }

    const q = BQ.build_query(viz);
    console.log(q);
    const body: ApiBQQuery = {
      project_id: table.project_id,
      dataset_id: table.dataset_id,
      table_id: table.id,
      query: q,
    };
    Ajax.post("/api/v1/bq_query", function (data: ApiBQResponse) {
      for (let i = 0; i < data.rows.length; i++) {
        const row = data.rows[i];
        for (let j = 0; j < row.length; j++) {
          const el = row[j];
          cols[j].rows.push(el);
        }
      }
      viz.data = cols;
      cb();
    }, body, function (status: number, data: ApiErrorResponse) {
      console.error(status, data.message);
      error_cb(data);
    });
  }

}